import { Box, Divider, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useState } from 'react';

import { Button, TextField } from '@/components';
import { MemberStates, Modals } from '@/constants';
import { useForm } from '@/hooks';
import { AnalyticsService, EVENT_CONSTANTS } from '@/services';
import {
  uiActions,
  useAppDispatch,
  useEditOrganizationMemberMutation,
  useReInviteOrganizationMembersMutation,
} from '@/store';
import { OrganizationMember } from '@/types';
import { SnackbarUtils } from '@/utils';

import { getMemberStateLabel } from '../utils';
import { UserFriendlyDateWithTooltip } from './UserFriendlyDateWithTooltip';

export type EditMemberFormState = {
  email: string;
  name: string;
};

interface EditMemberFormProps {
  member: OrganizationMember;
  goBack: () => void;
  isArchivingDisabled: boolean;
}

const location = {
  page: EVENT_CONSTANTS.PAGE.MEMBER_MANAGEMENT,
  component: EVENT_CONSTANTS.COMPONENT.EDIT_MEMBER_MODAL,
};

export const EditMemberForm = ({
  member,
  goBack,
  isArchivingDisabled,
}: EditMemberFormProps) => {
  const {
    organizationMemberId,
    name,
    organizationEmail,
    userEmail,
    state,
    lastActivityDate,
    lastActivityType,
    organizationMemberCreatedAt,
  } = member;

  const dispatch = useAppDispatch();
  const [nameError, setNameError] = useState<string>();
  const { form, errors, validateForm, onChange } = useForm<EditMemberFormState>(
    {
      email: organizationEmail,
      name: name,
    },
  );
  const isDirty = form.email !== organizationEmail || form.name !== name;

  const [reinviteOrganizationMembers] =
    useReInviteOrganizationMembersMutation();
  const [editOrganizationMember, { isLoading: isSubmitting }] =
    useEditOrganizationMemberMutation();

  const handleSubmit = async () => {
    const isEmailValid = validateForm(['email']);
    const isNameValid = !!form.name?.trim();
    if (!isNameValid) {
      setNameError('Name is required');
    }
    if (!isEmailValid || !isNameValid) {
      return;
    }

    try {
      await editOrganizationMember([
        {
          organizationMemberId,
          email: form.email.trim(),
          name: form.name.trim(),
        },
      ]).unwrap();

      if (
        organizationEmail !== form.email &&
        state === MemberStates.PLAN_ASSIGNED
      ) {
        await reinviteOrganizationMembers([organizationMemberId]);
      }
      SnackbarUtils.success('Member details updated!');
      goBack();
    } catch {
      SnackbarUtils.error(
        'Something went wrong. Please try again or contact support.',
      );
    }
  };

  const handleRemove = async () => {
    AnalyticsService.removeMember({
      location: {
        ...location,
        button: EVENT_CONSTANTS.BUTTON.REMOVE,
      },
      organizationMemberIds: [organizationMemberId],
    });
    dispatch(
      uiActions.setActiveModal({
        name: Modals.MEMBER_ARCHIVE,
        params: {
          organizationMemberIds: [organizationMemberId],
          referrer: {
            ...location,
            button: EVENT_CONSTANTS.BUTTON.REMOVE,
          },
        },
      }),
    );
    goBack();
  };

  const { label, color } = getMemberStateLabel(state);
  return (
    <Box sx={styles.root}>
      <Box sx={styles.header}>
        <Typography variant="h2">{name}</Typography>
        <Typography variant="body3">{organizationEmail}</Typography>
        {userEmail && organizationEmail !== userEmail && (
          <Typography variant="body3">
            {userEmail}{' '}
            <Typography variant="body3" component="span" color="text.light">
              (login email)
            </Typography>
          </Typography>
        )}
        <Divider sx={styles.divider} />
        <Box sx={styles.table}>
          <Box sx={styles.tableColumn}>
            <Typography variant="body3" sx={styles.tableColumnLabel}>
              Status
            </Typography>
            <Box sx={styles.stateLabel}>
              <Typography variant="h4" lineHeight="18px" color={color}>
                &bull;&nbsp;
              </Typography>
              <Typography
                sx={styles.stateLabel}
                variant="body3"
                id="status-label">
                {label}
              </Typography>
            </Box>
          </Box>
          <Box sx={styles.tableColumn}>
            <Typography variant="body3" sx={styles.tableColumnLabel}>
              Activity
            </Typography>
            <UserFriendlyDateWithTooltip
              activityType={lastActivityType}
              date={lastActivityDate}
            />
          </Box>
          <Box sx={styles.tableColumn}>
            <Typography variant="body3" sx={styles.tableColumnLabel}>
              Added
            </Typography>
            <Typography variant="body3">
              {organizationMemberCreatedAt
                ? format(organizationMemberCreatedAt, 'MMM d, yyyy')
                : ''}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={styles.main}>
        <Typography variant="h4">Details</Typography>
        <TextField
          id="email"
          fullWidth
          label="Email"
          value={form.email}
          error={!!errors.email}
          helperText={errors.email}
          onChange={onChange}
        />
        <TextField
          id="name"
          fullWidth
          label="Name"
          value={form.name}
          error={!!nameError}
          helperText={nameError}
          onChange={e => {
            setNameError(undefined);
            onChange(e as React.ChangeEvent<HTMLInputElement>);
          }}
        />
      </Box>
      <Box sx={styles.footer}>
        <Button
          label="Remove"
          variant="secondary"
          onClick={handleRemove}
          fullWidth={false}
          skinny={false}
          disabled={isArchivingDisabled}
          sx={styles.removeButton}
        />
        <Box sx={styles.footerPrimaryActions}>
          <Button
            label="Cancel"
            variant="secondary"
            onClick={goBack}
            fullWidth={false}
            skinny={false}
          />
          <Button
            label="Save"
            variant="primary"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            fullWidth={false}
            skinny={false}
            disabled={!isDirty}
            sx={styles.submitButton}
          />
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    width: { xs: 'auto', md: 560 },
  },
  stateLabel: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  divider: {
    my: 1.5,
    borderColor: 'grey100',
  },
  table: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: { xs: 'column', sm: 'row' },
    gap: 1,
  },
  tableColumn: {
    display: 'flex',
    flexDirection: { xs: 'row', sm: 'column' },
    gap: 1,
  },
  tableColumnLabel: {
    flexBasis: { xs: '50px', sm: 'auto' },
  },
  header: {
    mb: 4,
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    mb: 4,
  },
  footer: {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    justifyContent: 'space-between',
    gap: 4,
  },
  footerPrimaryActions: {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    gap: 2,
  },
  submitButton: {
    minWidth: 120,
  },
  removeButton: {
    display: { xs: 'none', sm: 'block' },
  },
};
